import React, { useState, useEffect } from 'react'
import { X, AlertTriangle, ArrowRight, CheckCircle2, Info } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const RefugarModal = ({ isOpen, onClose, onSuccess, organizadorId, lote }) => {
  const [qtdRefugo, setQtdRefugo] = useState('')
  const [loading, setLoading] = useState(false)
  const [confirmando, setConfirmando] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setQtdRefugo('')
      setConfirmando(false)
    }
  }, [isOpen])

  if (!isOpen || !lote) return null

  const qtd = parseInt(qtdRefugo, 10)
  const qtdValida = Number.isInteger(qtd) && qtd >= 1 && qtd < lote.qtd
  const qtdRestante = qtdValida ? lote.qtd - qtd : null
  const isFaturado = lote.status === 'FATURADO'

  // Gera sufixo visual (A, B, …) para preview — o backend determina o real
  const sufixoPreview = 'A'
  const m = lote.numero_lote?.match(/^(\d+)/)
  const baseNum = m ? m[1] : lote.numero_lote
  const novoNumero = `${baseNum}${sufixoPreview}`

  // Recálculo estimado do valor (se faturado)
  const valorUni = lote.valor_venda_unidade ? parseFloat(lote.valor_venda_unidade) : null
  const novoTotalEstimado = valorUni && qtdValida ? valorUni * qtdRestante : null

  const handleConfirmar = () => {
    if (!qtdValida) return
    setConfirmando(true)
  }

  const handleSubmit = async () => {
    setLoading(true)
    try {
      const resultado = await lotesService.refugarLote(organizadorId, lote.id, qtd)
      const { lote_original, lote_refugo } = resultado

      const msg = lote_original.era_faturado
        ? `Lote ${lote_original.numero_lote} → ${lote_original.qtd} animais (refaturado). Lote ${lote_refugo.numero_lote} criado com ${lote_refugo.qtd} animais.`
        : `Lote ${lote_original.numero_lote} → ${lote_original.qtd} animais. Lote ${lote_refugo.numero_lote} criado com ${lote_refugo.qtd} animais.`

      toast.success(msg, { duration: 5000 })
      onSuccess()
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao realizar refugo'
      toast.error(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                className="w-5 h-5 text-orange-600">
                <path d="M6 3v18M18 3v18M3 9h18M3 15h18" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Refugar Lote</h2>
              <p className="text-xs text-gray-500">
                Lote {lote.numero_lote} · {lote.qtd} {lote.qtd === 1 ? 'animal' : 'animais'}
                {isFaturado && <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold">FATURADO</span>}
              </p>
            </div>
          </div>
          <button onClick={onClose} disabled={loading}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {!confirmando ? (
            <div className="p-6 space-y-5">
              {/* Aviso faturado */}
              {isFaturado && (
                <div className="flex gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    Este lote está <strong>faturado</strong>. Ao refugar, o valor de venda total será
                    recalculado automaticamente com base na nova quantidade.
                  </p>
                </div>
              )}

              {/* Input quantidade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Quantos animais deseja refugar?
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min={1}
                    max={lote.qtd - 1}
                    value={qtdRefugo}
                    onChange={e => setQtdRefugo(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-gray-900 text-lg font-semibold
                      focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none transition-all
                      placeholder:text-gray-300 placeholder:font-normal"
                    placeholder="0"
                    autoFocus
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                    de {lote.qtd}
                  </span>
                </div>
                {qtdRefugo !== '' && !qtdValida && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {parseInt(qtdRefugo) >= lote.qtd
                      ? `Deve sobrar ao menos 1 animal (máx: ${lote.qtd - 1})`
                      : 'Informe uma quantidade válida maior que zero'}
                  </p>
                )}
              </div>

              {/* Preview do resultado */}
              {qtdValida && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Resultado do refugo</p>
                  <div className="flex items-center gap-3">
                    {/* Lote original */}
                    <div className="flex-1 border border-gray-200 rounded-xl p-3.5 bg-gray-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-gray-500">Lote original</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          isFaturado ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>{lote.status}</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900">{lote.numero_lote}</p>
                      <p className="text-sm text-gray-600">
                        <span className="line-through text-gray-400">{lote.qtd}</span>
                        <span className="ml-2 font-semibold text-gray-900">{qtdRestante} animais</span>
                      </p>
                      {novoTotalEstimado !== null && (
                        <p className="text-xs text-gray-500 mt-1">
                          ≈ {formatCurrency(novoTotalEstimado)}
                        </p>
                      )}
                    </div>

                    <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />

                    {/* Novo lote */}
                    <div className="flex-1 border border-orange-200 rounded-xl p-3.5 bg-orange-50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-orange-600">Novo lote</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">NOVO</span>
                      </div>
                      <p className="text-lg font-bold text-orange-700">{novoNumero}*</p>
                      <p className="text-sm font-semibold text-orange-800">{qtd} animais</p>
                      {lote.valor_minimo_unidade && (
                        <p className="text-xs text-orange-600 mt-1">
                          Mín: {formatCurrency(lote.valor_minimo_unidade)}/cab
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    *O número real será o próximo sufixo disponível (A, B, C…)
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Tela de confirmação */
            <div className="p-6 space-y-5">
              <div className="flex flex-col items-center text-center gap-3 py-2">
                <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-orange-500" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Confirmar refugo</h3>
                  <p className="text-sm text-gray-500 mt-1">Esta ação não pode ser desfeita diretamente.</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Lote original</span>
                  <span className="font-semibold">{lote.numero_lote}: {lote.qtd} → {qtdRestante} animais</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Novo lote (refugo)</span>
                  <span className="font-semibold text-orange-700">{novoNumero}* — {qtd} animais (NOVO)</span>
                </div>
                {isFaturado && novoTotalEstimado !== null && (
                  <div className="flex justify-between pt-1 border-t border-gray-200">
                    <span className="text-gray-500">Novo valor total estimado</span>
                    <span className="font-semibold text-green-700">{formatCurrency(novoTotalEstimado)}</span>
                  </div>
                )}
              </div>

              {isFaturado && (
                <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    O valor de venda do lote original será recalculado com base em {qtdRestante} animais.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          {!confirmando ? (
            <>
              <button onClick={onClose} disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleConfirmar}
                disabled={!qtdValida}
                className="px-5 py-2 text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg
                  transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                Continuar
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirmando(false)} disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                Voltar
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-5 py-2 text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white rounded-lg
                  transition-colors disabled:opacity-50 flex items-center gap-2 min-w-[130px] justify-center">
                {loading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Processando…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Confirmar Refugo
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default RefugarModal
